import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import Stack from '@mui/joy/Stack';
import Button from '@mui/joy/Button';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import Divider from '@mui/joy/Divider';
import { CollabProps } from 'stores/collab_store';

import LockedFeature from 'components/LockedFeature';
import Success from 'components/Success';
import Error from 'components/Error';
import IdentityAvatar from 'components/IdentityAvatar';

import useDedicatedAssignment from 'hooks/useDedicatedAssignment';
import useUserIdentity from 'hooks/useUserIdentity';
import useIdentity from 'hooks/useIdentity';
import useCollaborators from 'hooks/useCollaborators';
import useCollabInvite from 'hooks/useCollabInvite';

import useWorkKey from 'hooks/useWorkKey';
import useRoles from 'hooks/useRoles';
import { IdentityProps } from 'stores/identity_store';
import { Textarea } from '@mui/joy';
import FancyIcon from './FancyIcon';


const TeamMemberInviteButton = observer(({ associated_type, associated_uuid, identity }: { associated_type: string, associated_uuid: string, identity: IdentityProps }) => {
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const { inviteTeamMember } = useCollabInvite();
    const workKey = useWorkKey();
    const [inviting, setInviting] = useState(false);

    const [message, setMessage] = useState("");

    function doInviteTeamMember() {
        setSending(true);
        if(!workKey) return;

        console.log("Message", message);
        inviteTeamMember(workKey, associated_type, associated_uuid, identity.uuid, message)
            .then(() => {
                setSending(false);
                setSent(true);
                Success("Invite sent");
            })
            .catch(() => {
                setSending(false);
                Error("Could not send invite");
            });
    }


    if(inviting) {
        return (
            <Stack justifyContent="center" spacing={1}>
            <Typography level="body-sm">Inviting {identity.name}</Typography>
                    <IdentityAvatar interactive={false} size="inline" identityKey={identity.uuid} />

                    <Textarea onChange={(e) => setMessage(e.target.value)} placeholder="Add a message" />

                {sending &&
                    <Button
                        disabled
                        variant="soft">Sending</Button>}
                {sent &&
                    <Button
                        disabled
                        variant="soft">Sent!</Button>}
                {(!(sending || sent)) &&
                    <Button
                        size="sm"
                        onClick={() => { doInviteTeamMember() }}
                        variant="soft">Invite</Button>
                        }
                        <Button onClick={() => setInviting(!inviting)} variant="plain" size="sm" startDecorator={<FancyIcon name="Close" />}>Cancel</Button>
                </Stack>
        );
    }

    return (
        <Stack py={1} direction="row" justifyContent="space-between">
            <IdentityAvatar interactive={false} size="inline" identityKey={identity.uuid} />
            <Button onClick={() => setInviting(!inviting)} variant="plain" size="sm">Invite</Button>
        </Stack>
    )

});


export const TeamSelector = observer(({ associated_type, associated_uuid, ignoreIdentities }: { associated_type: string, associated_uuid: string, ignoreIdentities: string[] }) => {

    const { currentIdentity } = useUserIdentity();
    const { fetchListByTeam, identities } = useIdentity();
    const { hasRole } = useRoles();

    const team_members = identities
        // all team members
        .filter((i) => i.team == currentIdentity.team)
        // not us
        .filter((i) => i.uuid != currentIdentity.uuid)
        // not explicitly ignored
        .filter((i) => !ignoreIdentities.includes(i.uuid)) || [];

    useEffect(() => {
        if(hasRole('support')) {
            fetchListByTeam(currentIdentity.uuid, currentIdentity.team);
        }
    }, [currentIdentity.uuid, currentIdentity.team]);

    if (!team_members.length) {
        return null;
    }

    return (
        <Stack p={1} >
            <Typography level="body-sm">Invite a Team Member</Typography>
            <Stack pt={1} divider={<Divider />} >
                {team_members.map((m) =>
                    <TeamMemberInviteButton
                        associated_type={associated_type}
                        associated_uuid={associated_uuid}
                        identity={m} />
                )}
            </Stack>
        </Stack>
    );
});

const DedicatedAssignmentButton = (({ collab, workKey, assistant_identity }: {collab: string, workKey: string, assistant_identity: string}) => {

    const [inviting, setInviting] = useState(false);
    const [done, setDone] = useState(false);
    const { inviteDedicatedSpecific } = useCollabInvite();

    function createdDedicatedInvite() {
        if (!collab) {
            Error("Could not create invite, no collab?");
            return;
        }
        setInviting(true);
        inviteDedicatedSpecific(workKey, collab, assistant_identity)
            .then((res) => {
                console.log(res);
                Success("Created invite")
            })
            .catch(() => {
                Error("Could not create invite");
            })
            .finally(() => {
                setInviting(false);
                setDone(true);
            });
    }

    if(done) {
        return null;
    }

    return (
        <Stack spacing={1} p={1}>
            <IdentityAvatar size="inline" identityKey={assistant_identity} />
            <Stack direction="row" justifyContent="flex-end">
                <Button variant="plain" size="sm" onClick={createdDedicatedInvite} disabled={inviting}>Invite</Button>
            </Stack>
        </Stack>
    );

});

const DedicatedInviters = observer(({ collab }: { collab: CollabProps }) => {
    const { getDedicatedAssignmentsByIdentity, fetchDedicatedAssignmentsByIdentity } = useDedicatedAssignment();
    const { getCollaborators } = useCollaborators();
    const { currentIdentity } = useUserIdentity();

    const workKey = useWorkKey();
    const collabIdentities = getCollaborators(collab.key).map((m) => m.identity);

    // List of this users dedicated asssitants who are not already assigned
    var assignments = getDedicatedAssignmentsByIdentity(collab.identity)
        .filter((a) => !collabIdentities.includes(a.assistant_identity));

    // if it's the client, let's only let them assign when ranking == 1
    if(currentIdentity.role == 'client') {
        assignments = assignments.filter((m) => m.ranking == 1);
    }

    useEffect(() => {
        if (collab.identity && workKey) {
            fetchDedicatedAssignmentsByIdentity(workKey, collab.identity);
        }

    }, [workKey,
        collab.identity]);



    return (
        <>
        {assignments.map((assignment) => {
            return <DedicatedAssignmentButton
                       workKey={workKey}
                       collab={collab.uuid}
                       assistant_identity={assignment.assistant_identity}
            />

        })
        }
        </>

    );
});

// const DedicatedInviters = observer(({ collab }: { collab: CollabProps }) => {
//     const { hasDedicatedAssistant, isDedicatedFor, fetchDedicatedAssignmentsByIdentity } = useDedicatedAssignment();
//     const [invitingDedicated, setInvitingDedicated] = useState(false);
//     const [showInviteDedicated, setShowInviteDedicated] = useState(false);
//     const { getCollaborators } = useCollaborators();
//     const { currentIdentity } = useUserIdentity();
//     const { inviteDedicated } = useCollabInvite();
//     const collabIdentities = getCollaborators(collab.key).map((m) => m.identity);

//     const updateDedicatedInfo = useCallback(() => {
//         if (currentIdentity.key && currentIdentity.key == collab?.identity) {
//             if (!collab?.key) return;
//             if (!collab?.identity) return;
//             if (!currentIdentity?.key) return;
//             const c = getCollaborators(collab.key).map((m) => m.identity);

//             const showIt = hasDedicatedAssistant(currentIdentity.key) &&
//                 !c.find((i) => isDedicatedFor(i, currentIdentity.key));

//             setShowInviteDedicated(showIt);
//         }
//     }, [collab.key,
//         collab.identity,
//         getCollaborators,
//         currentIdentity.key,
//         hasDedicatedAssistant,
//         isDedicatedFor]);

//     useEffect(() => {
//         if (currentIdentity.key) {
//             fetchDedicatedAssignmentsByIdentity(currentIdentity.key);
//         }

//     }, [currentIdentity.key,
//         updateDedicatedInfo,
//         fetchDedicatedAssignmentsByIdentity,
//         hasDedicatedAssistant]);

//     useEffect(() => {
//         updateDedicatedInfo();
//     }, [collabIdentities, updateDedicatedInfo]);


//     function createdDedicatedInvite() {
//         if (!collab || !collab.key) {
//             Error("Could not create invite, no collab?");
//             return;
//         }
//         setInvitingDedicated(true);
//         inviteDedicated(collab.key, currentIdentity.key)
//             .then(() => {
//                 Success("Created invite")
//             })
//             .catch(() => {
//                 Error("Could not create invite");
//             })
//             .finally(() => {
//                 setInvitingDedicated(false);
//             });
//     }




//     return (
//         <>
//                 {showInviteDedicated &&
//                     <Tooltip title="Add your dedicated assistant to this collab" arrow placement="left">
//                         <Button
//                             variant="plain"
//                             disabled={invitingDedicated}
//                             onClick={() => { if (!invitingDedicated) { createdDedicatedInvite(); } }}>
//                             {invitingDedicated ? "Inviting" : "Add Your Dedicated"}
//                         </Button>
//                     </Tooltip>
//                 }
//         </>

//     );
// });

const CollabInviteFeatureFull = observer(({ collab, children=null }: { collab: CollabProps, children: React.ReactNode | null }) => {
    const { isDedicatedFor } = useDedicatedAssignment();

    const { getInvitesForCollab, create, dedicatedInviteDayOne } =
        useCollabInvite();
    const { currentIdentity } = useUserIdentity();
    const { getCollaborators } = useCollaborators();

    const { hasRole } = useRoles();

    const invites = getInvitesForCollab(collab.key);
    const collabIdentities = getCollaborators(collab.key).map((m) => m.identity);
    const activeInvites = invites.filter((invite) =>
        ['available', 'invited'].includes(invite.status)
    );
    const activeInviteIdentities = activeInvites.map((m) => m.identity);

    const ignoreIdentities = [...collabIdentities, ...activeInviteIdentities]


    // const [showingMutualSelector, setShowingMutualSelector] = useState(false);

    const showInviteDayOnes = false && collab.identity == currentIdentity.key;
    // const showInviteMutuals = false && mutualCollaborators.length > 0;



    /* Don't show invites for support collabs */
    if(collab.variant == 'support' && !hasRole('support')) {
        return null;
    }



    function createInvite() {
        if (!collab || !collab.key) {
            Error("Could not create invite, no collab?");
            return;
        }
        create(collab.key)
            .then(() => {
                Success("Created invite")
            })
            .catch((err) => {
                console.error(err);
                if (err.message.startsWith('not enough credit')) {
                    Error("You don't have enough credits to create an invite");
                }
                else {
                    if (err.message) {
                        Error(`Error creating invite: ${err.message}`);
                    } else {
                        Error("Could not create invite");
                    }
                }
            });
    }

    function dedicatedCreateInvite() {
        if (!collab || !collab.key) {
            Error("Could not create invite, no collab?");
            return;
        }
        dedicatedInviteDayOne(collab.key, currentIdentity.key)
            .then(() => {
                Success("Created invite")
            })
            .catch((err) => {
                if (err.message.startsWith('not enough credit')) {
                    Error("This client doesn't have enough credits to create an invite.");
                }
                else {
                    if (err.message) {
                        Error(`Error creating invite: ${err.message}`);
                    } else {
                        Error("Could not create invite");
                    }
                }
            });
    }




    return (
        <LockedFeature
            color="neutral"
            chip={true}
            lockedIconName="PersonAdd"
            unlockedIconName="PersonRemove"
            loreSlug="feature/collab/inviting-collaborators"
            copy="Add Collaborator">


            <Stack justifyContent="center" spacing={1}>
                <DedicatedInviters collab={collab} />

                {/* unused
                {showInviteMutuals &&
                    <Stack spacing={1}>
                        {showingMutualSelector &&
                            <MutualInviteSelector ignoreIdentities={ignoreIdentities} collab={collab} />
                        }

                        <Tooltip title="Invite one of your mutuals" arrow placement="left">
                            <Button variant="plain" onClick={() => { setShowingMutualSelector(!showingMutualSelector) }}>
                                {showingMutualSelector ? "Nevermind" : "Invite a Mutual"}
                            </Button>
                        </Tooltip>
                    </Stack>
                  */}

                {showInviteDayOnes &&
                    <Tooltip title="We'll find another person to work with on this collab" arrow placement="left">
                        <Button variant="plain" onClick={() => { createInvite() }}>Add a Day One</Button>
                    </Tooltip>
                }

                {showInviteDayOnes && isDedicatedFor(currentIdentity.key, collab.identity) &&
                    <Tooltip title="This will send it to the board for someone to claim. You'll still be here."
                        arrow placement="left">
                        <Button
                            variant="plain"
                            onClick={() => { dedicatedCreateInvite() }}>Invite a Day One</Button>
                    </Tooltip>
                }

                {hasRole('support') &&
                    <Stack alignItems="center">
                        <TeamSelector ignoreIdentities={ignoreIdentities} associated_type="collab" associated_uuid={collab.uuid} />
                    </Stack>
                }
            </Stack>

            { children }

        </LockedFeature>
    );
});

const CollabInviteFeature = observer(({ collab, children=null }: { collab: CollabProps, children?: React.ReactNode | null }) => {

    const { currentIdentity } = useUserIdentity();
    const workKey = useWorkKey();
    const { hasRole } = useRoles();
    const { hasDedicatedAssistant, fetchDedicatedAssignmentsByIdentity, isDedicatedFor } = useDedicatedAssignment();
    const [youDedicated, setYouDedicated] = useState(isDedicatedFor(currentIdentity.uuid, collab.identity));


    useEffect(() => {
        if (currentIdentity.uuid) {
            // console.log("Fetching dedicated assignments for", currentIdentity.uuid);
            fetchDedicatedAssignmentsByIdentity(workKey, collab.identity)
                .then(() => {
                    // console.log("Fetched dedicated assignments", logs);
                    console.log("Setting youDedicated", isDedicatedFor(currentIdentity.uuid, collab.identity));
                        setYouDedicated(isDedicatedFor(currentIdentity.uuid, collab.identity));
                });

        }

    }, [currentIdentity.uuid,
        fetchDedicatedAssignmentsByIdentity,
        hasDedicatedAssistant]);

    if(!youDedicated && (currentIdentity.uuid != collab.identity) && !hasRole('support')) {
        return null;
    }

    return (
        <CollabInviteFeatureFull collab={collab}>
            { children }
        </CollabInviteFeatureFull>
    );
});
export default CollabInviteFeature;
